'use client'

import useTranslations from '@snowball-tech/common/hooks/useTranslations'
import { Paper } from '@snowball-tech/fractal/dist/src/components/Paper'
import { Typography } from '@snowball-tech/fractal/dist/src/components/Typography'
import { cj } from '@snowball-tech/fractal/dist/src/styles/helpers'
import Image from 'next/image'
import { useSearchParams } from 'next/navigation'
import Parallax from 'parallax-js'

import {
  type ReactNode,
  createElement,
  useEffect,
  useRef,
  useState,
} from 'react'

import { SNOWBALL_BOOK_URL } from '@/constants'
import ImageBankNotes from '@/images/banknotes.svg'
import ImageCoin from '@/images/coin.svg'
import ImageSnowflake from '@/images/snowflake.svg'

import HomeSubscribeForm from './SubscribeForm'

type Props = {
  withPs?: boolean
}
export default function HomePageHero({ withPs = false }: Props) {
  const searchParams = useSearchParams()
  const $t = useTranslations()

  const [isAnimationFinished, setIsAnimationFinished] = useState(false)
  const [isSubscribeSubmitted, setIsSubscribeSubmitted] = useState(
    searchParams.get('message') === 'signup-successfull',
  )

  const sceneRef = useRef<HTMLDivElement | null>(null)

  const parallax = useRef<Parallax | null>(null)
  useEffect(() => {
    if (sceneRef.current) {
      parallax.current = new Parallax(sceneRef.current, {
        onReady: () => {
          setIsAnimationFinished(true)
        },
        pointerEvents: true,
        selector: '.layer',
      })
    }

    return () => {
      parallax.current?.destroy()
    }
  }, [sceneRef])

  return (
    <div id="hero" className="relative w-full">
      <div
        id="parallax-scene"
        ref={sceneRef}
        className="relative w-full bg-decorative-purple-70"
      >
        <div className="relative flex w-full flex-col items-stretch lg:flex-row">
          <div className="flex w-full bg-repeat [background-image:linear-gradient(to_bottom,#e2ceff_1px,transparent_1px),linear-gradient(to_right,#e2ceff_1px,transparent_1px)] [background-position-x:calc(var(--size-spacing-3)*-1)] [background-size:var(--size-spacing-6)_var(--size-spacing-6)] lg:w-1/2 lg:justify-end xl:w-7/12">
            <div className="flex size-full max-w-screen-md items-center justify-center px-3 pb-14 pt-8 sm:px-7 lg:justify-end lg:px-5 lg:py-14">
              <div className="z-10 flex flex-col gap-3 lg:mr-5">
                <Typography
                  className="!text-display-wide-xxs !leading-display-wide-xxs xl:!text-display-wide-md xl:!leading-display-wide-md"
                  element="h1"
                  variant="display-wide"
                >
                  {$t.rich('home.hero.title')}
                </Typography>

                <div className="flex flex-col gap-1">
                  <Typography variant="body-1-median">
                    {$t.rich('home.hero.description')}
                  </Typography>

                  {withPs && (
                    <Typography variant="body-2">
                      {$t.rich('home.hero.ps', {
                        bookLink: (content: ReactNode) =>
                          createElement(
                            Typography,
                            {
                              href: SNOWBALL_BOOK_URL,
                              target: '_blank',
                              variant: 'body-2-link',
                            },
                            content,
                          ),
                      })}
                    </Typography>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="-mt-4 flex w-full justify-center bg-decorative-purple-70 px-3 pb-8 [background:linear-gradient(to_bottom,transparent_0%,var(--color-decorative-purple-70)_var(--size-spacing-5))] sm:px-7 lg:mt-unset lg:w-fit lg:flex-1 lg:items-center lg:justify-start lg:px-unset lg:py-14 lg:pr-5 lg:[background:linear-gradient(to_right,transparent_0%,var(--color-decorative-purple-70)_var(--size-spacing-4))]">
            <Paper
              id="subscribe-form"
              className={cj(
                'relative flex size-fit flex-col gap-2 border-2 px-2 py-4 lg:px-4 xl:max-w-[480px]',
              )}
              elevation="3"
            >
              <Typography element="p" variant="heading-4">
                {$t(
                  isSubscribeSubmitted
                    ? 'subscribe.title.submitted'
                    : 'home.hero.subscribe.title',
                )}
              </Typography>

              {!isSubscribeSubmitted && (
                <Typography>{$t('home.hero.subscribe.description')}</Typography>
              )}

              <HomeSubscribeForm
                origin="hero"
                onSubmit={() => setIsSubscribeSubmitted(true)}
              />

              <div
                className="layer !absolute !-top-8 !left-1 !hidden xl:!-left-10 xl:!top-7 xl:!block"
                data-depth="0.04"
              >
                <Image
                  alt=""
                  className={cj(
                    'transition-transform duration-1500 xl:scale-75',
                    isAnimationFinished
                      ? 'rotate-[80deg] xl:rotate-[65deg]'
                      : '-translate-y-3 rotate-[52deg]',
                  )}
                  src={ImageCoin}
                />
              </div>

              <div
                className="layer !absolute !-top-8 !left-3/4 lg:!-top-10 lg:!right-3 xl:!-top-12"
                data-depth="0.06"
              >
                <Image
                  alt=""
                  className={cj(
                    'transition-transform duration-1500',
                    isAnimationFinished
                      ? 'lg:scale-[1.7] xl:scale-[2]'
                      : 'translate-x-3 rotate-[20deg] lg:translate-y-3 lg:scale-110 xl:translate-y-5',
                  )}
                  src={ImageSnowflake}
                />
              </div>

              <div
                className="layer !absolute !-left-5 !top-unset -bottom-6 !hidden xl:!block"
                data-depth="0.09"
              >
                <Image
                  alt=""
                  className={cj(
                    'transition-transform duration-1500',
                    isAnimationFinished ? '' : 'translate-y-2 rotate-[20deg]',
                  )}
                  src={ImageCoin}
                />
              </div>

              <div className="absolute -bottom-10 -left-2 lg:hidden">
                <Image
                  alt=""
                  className="-translate-y-2 translate-x-2 rotate-[20deg] scale-90 transition-transform duration-1000 group-hover:translate-x-0 group-hover:translate-y-0 group-hover:rotate-0"
                  src={ImageBankNotes}
                />
              </div>
            </Paper>
          </div>
        </div>
      </div>

      <div className="absolute -bottom-5 left-1/2 z-20 hidden lg:block">
        <Image
          alt=""
          className="-translate-y-2 translate-x-2 rotate-[20deg] scale-110 transition-transform duration-1000 group-hover:translate-x-0 group-hover:translate-y-0 group-hover:rotate-0"
          src={ImageBankNotes}
        />
      </div>
    </div>
  )
}
